import React, { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useInstaller } from '../../contexts/InstallerContext';
import { useJobs } from '../../hooks/useJobs';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase-init';
import { sendNoteAddedEmail } from '../../services/installer-notification-service';

export default function AddNoteForm({ isOpen, onClose, job }) {
  const installer = useInstaller();
  const { refreshJobs } = useJobs();
  
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!noteText.trim()) {
      newErrors.noteText = 'Please enter a note';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);

    try {
      const jobRef = doc(db, 'jobs', job.id);
      
      const newNote = {
        text: noteText.trim(),
        addedBy: installer.name,
        addedByUid: installer.uid,
        timestamp: Timestamp.now()
      };

      await updateDoc(jobRef, {
        installerNotes: arrayUnion(newNote)
      });

      await sendNoteAddedEmail(job, installer, noteText);

      console.log('✅ Note added successfully');
      
      if (refreshJobs) {
        await refreshJobs();
      }
      
      setNoteText('');
      setErrors({});
      onClose();
    } catch (error) {
      console.error('❌ Error adding note:', error);
      setErrors({ submit: 'Failed to add note. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="📝 Add Internal Note"
      size="medium"
    >
      <div style={{
        padding: '24px'
      }}>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#2b2b2b',
          marginBottom: '8px'
        }}>
          #{job.contractNumber} - {job.customerName || job.name}
        </div>

        <div style={{
          fontSize: '14px',
          color: '#666',
          marginBottom: '24px'
        }}>
          Add a quick note about this job. Your team and managers will be notified.
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#2b2b2b',
            marginBottom: '8px'
          }}>
            Note *
          </label>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Quick note about this job..."
            autoFocus
            style={{
              width: '100%',
              minHeight: '120px',
              padding: '12px',
              border: '2px solid #e5e5e5',
              borderRadius: '12px',
              fontSize: '16px',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#8B4513'}
            onBlur={(e) => e.target.style.borderColor = '#e5e5e5'}
          />

          {errors.noteText && (
            <div style={{
              color: '#dc2626',
              fontSize: '13px',
              marginTop: '8px',
              fontWeight: '500'
            }}>
              {errors.noteText}
            </div>
          )}
        </div>

        {errors.submit && (
          <div style={{
            padding: '12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            color: '#dc2626',
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            {errors.submit}
          </div>
        )}

        <div style={{
          padding: '12px',
          background: 'rgba(139, 69, 19, 0.1)',
          border: '1px solid rgba(139, 69, 19, 0.2)',
          borderRadius: '12px',
          fontSize: '13px',
          color: '#713f12',
          fontWeight: '500'
        }}>
          📧 Managers will be notified about this note via email.
        </div>
      </div>

      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e5e5',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        background: '#fafafa'
      }}>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={submitting}
          style={{ minHeight: '44px' }}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleSubmit}
          disabled={submitting || !noteText.trim()}
          style={{
            background: '#8B4513',
            minHeight: '44px'
          }}
        >
          {submitting ? 'Saving...' : 'Save Note'}
        </Button>
      </div>
    </Modal>
  );
}